import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import {ArrowLeft, Download, FileSignature, Upload, CheckCircle, Plus, Trash2, FileUp} from 'lucide-react'
import { lumi } from '../lib/lumi'
import jsPDF from 'jspdf'

interface ItemOrcamento {
  descricao: string
  valor: string
}

interface PropostaImportada {
  _id: string
  nomeEmpresa: string
  cpfCnpjEmpresa?: string
  telefoneEmpresa?: string
  emailEmpresa?: string
  enderecoEmpresa?: string
  cidadeEmpresa?: string
  estadoEmpresa?: string
  cepEmpresa?: string
  websiteEmpresa?: string
  redesSociais?: string
  nomeCliente: string
  cpfCnpjCliente?: string
  emailCliente: string
  telefoneCliente: string
  enderecoCliente?: string
  servicoOferecido: string
  descricaoServico: string
  escopoDetalhado?: string
  prazo: string
  valor: string
  formaPagamento: string
  validadeProposta: number
  observacoes?: string
  status: string
  statusManual: string
}

export default function CriarContrato() {
  const navigate = useNavigate()
  
  // Dados da Empresa
  const [nomeEmpresa, setNomeEmpresa] = useState('')
  const [cpfCnpjEmpresa, setCpfCnpjEmpresa] = useState('')
  const [telefoneEmpresa, setTelefoneEmpresa] = useState('')
  const [emailEmpresa, setEmailEmpresa] = useState('')
  const [enderecoEmpresa, setEnderecoEmpresa] = useState('')
  const [cidadeEmpresa, setCidadeEmpresa] = useState('')
  const [estadoEmpresa, setEstadoEmpresa] = useState('')
  const [cepEmpresa, setCepEmpresa] = useState('')
  const [websiteEmpresa, setWebsiteEmpresa] = useState('')
  const [redesSociais, setRedesSociais] = useState('')
  
  // Dados do Cliente
  const [nomeCliente, setNomeCliente] = useState('')
  const [cpfCnpjCliente, setCpfCnpjCliente] = useState('')
  const [emailCliente, setEmailCliente] = useState('')
  const [telefoneCliente, setTelefoneCliente] = useState('')
  const [enderecoCliente, setEnderecoCliente] = useState('')
  
  // Informações do Contrato
  const [modeloContrato, setModeloContrato] = useState('')
  const [tituloServico, setTituloServico] = useState('')
  const [descricaoDetalhada, setDescricaoDetalhada] = useState('')
  const [entregasIncluidas, setEntregasIncluidas] = useState('')
  
  // Valores
  const [itensOrcamento, setItensOrcamento] = useState<ItemOrcamento[]>([{descricao: '', valor: ''}])
  const [valorMaoObra, setValorMaoObra] = useState('')
  const [valorMateriais, setValorMateriais] = useState('')
  const [desconto, setDesconto] = useState('')
  
  // Condições Contratuais
  const [prazoEntrega, setPrazoEntrega] = useState('')
  const [formaPagamento, setFormaPagamento] = useState('')
  const [garantia, setGarantia] = useState('')
  const [multaCancelamento, setMultaCancelamento] = useState('')
  const [condicoesCancelamento, setCondicoesCancelamento] = useState('')
  const [foro, setForo] = useState('')
  
  // Observações
  const [observacoes, setObservacoes] = useState('')
  const [naoIncluso, setNaoIncluso] = useState('')
  const [termosAdicionais, setTermosAdicionais] = useState('')
  
  // Comprovação de Aceite
  const [comprovanteAceite, setComprovanteAceite] = useState<File | null>(null)
  const [comprovanteUrl, setComprovanteUrl] = useState('')
  const [tipoComprovante, setTipoComprovante] = useState<'imagem' | 'audio' | 'documento' | ''>('')
  const [uploadLoading, setUploadLoading] = useState(false)
  
  // Importação de Proposta
  const [propostasAceitas, setPropostasAceitas] = useState<PropostaImportada[]>([])
  const [propostaSelecionada, setPropostaSelecionada] = useState('')
  const [mostrarImportacao, setMostrarImportacao] = useState(false)
  
  const [loading, setLoading] = useState(false)
  const [pdfGerado, setPdfGerado] = useState(false)

  // Carregar propostas aceitas
  useEffect(() => {
    const carregarPropostas = async () => {
      try {
        const user = lumi.auth.user
        if (!user) return
        
        const result = await lumi.entities.propostas.list({
          filter: {
            creator: user.userId,
            statusManual: 'fechada'
          }
        })
        
        setPropostasAceitas(result.list as PropostaImportada[])
      } catch (error) {
        console.error('Erro ao carregar propostas:', error)
      }
    }
    
    carregarPropostas()
  }, [])

  // Importar dados da proposta selecionada
  const importarProposta = () => {
    const proposta = propostasAceitas.find(p => p._id === propostaSelecionada)
    if (!proposta) return
    
    // Dados da empresa
    setNomeEmpresa(proposta.nomeEmpresa)
    setCpfCnpjEmpresa(proposta.cpfCnpjEmpresa || '')
    setTelefoneEmpresa(proposta.telefoneEmpresa || '')
    setEmailEmpresa(proposta.emailEmpresa || '')
    setEnderecoEmpresa(proposta.enderecoEmpresa || '')
    setCidadeEmpresa(proposta.cidadeEmpresa || '')
    setEstadoEmpresa(proposta.estadoEmpresa || '')
    setCepEmpresa(proposta.cepEmpresa || '')
    setWebsiteEmpresa(proposta.websiteEmpresa || '')
    setRedesSociais(proposta.redesSociais || '')
    
    // Dados do cliente
    setNomeCliente(proposta.nomeCliente)
    setCpfCnpjCliente(proposta.cpfCnpjCliente || '')
    setEmailCliente(proposta.emailCliente)
    setTelefoneCliente(proposta.telefoneCliente)
    setEnderecoCliente(proposta.enderecoCliente || '')
    
    // Dados do serviço
    setTituloServico(proposta.servicoOferecido)
    setDescricaoDetalhada(proposta.descricaoServico)
    setEntregasIncluidas(proposta.escopoDetalhado || '')
    setPrazoEntrega(proposta.prazo)
    setFormaPagamento(proposta.formaPagamento)
    setObservacoes(proposta.observacoes || '')
    
    // Valores
    const valorTotal = parseFloat(proposta.valor)
    setItensOrcamento([{descricao: proposta.servicoOferecido, valor: valorTotal.toFixed(2)}])
    
    setMostrarImportacao(false)
    alert('Dados da proposta importados com sucesso!')
  }

  const adicionarItem = () => {
    setItensOrcamento([...itensOrcamento, {descricao: '', valor: ''}])
  }

  const removerItem = (index: number) => {
    if (itensOrcamento.length > 1) {
      setItensOrcamento(itensOrcamento.filter((_, i) => i !== index))
    }
  }

  const atualizarItem = (index: number, campo: 'descricao' | 'valor', valor: string) => {
    const novosItens = [...itensOrcamento]
    novosItens[index][campo] = valor
    setItensOrcamento(novosItens)
  }

  const calcularTotal = () => {
    const totalItens = itensOrcamento.reduce((acc, item) => {
      const valor = parseFloat(item.valor.replace(',', '.')) || 0
      return acc + valor
    }, 0)
    const maoObra = parseFloat(valorMaoObra.replace(',', '.')) || 0
    const materiais = parseFloat(valorMateriais.replace(',', '.')) || 0
    const descontoVal = parseFloat(desconto.replace(',', '.')) || 0
    
    const subtotal = totalItens + maoObra + materiais
    const total = subtotal - descontoVal
    return total
  }

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    
    // Validar tipo de arquivo
    const tiposPermitidos = ['image/jpeg', 'image/png', 'image/webp', 'audio/mpeg', 'audio/wav', 'application/pdf']
    if (!tiposPermitidos.includes(file.type)) {
      alert('Tipo de arquivo não suportado. Use: imagem (jpg, png, webp), áudio (mp3, wav) ou PDF.')
      return
    }
    
    // Validar tamanho (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      alert('Arquivo muito grande. Tamanho máximo: 10MB')
      return
    }
    
    setUploadLoading(true)
    
    try {
      const result = await lumi.tools.file.upload([file])
      if (result && result.length > 0) {
        const url = typeof result[0] === 'string' ? result[0] : result[0].fileUrl
        setComprovanteUrl(url || '')
        setComprovanteAceite(file)
        
        // Detectar tipo
        if (file.type.startsWith('image/')) {
          setTipoComprovante('imagem')
        } else if (file.type.startsWith('audio/')) {
          setTipoComprovante('audio')
        } else {
          setTipoComprovante('documento')
        }
        
        alert('Comprovante enviado com sucesso!')
      }
    } catch (error) {
      console.error('Erro ao enviar comprovante:', error)
      alert('Erro ao enviar comprovante. Tente novamente.')
    } finally {
      setUploadLoading(false)
    }
  }

  const getClausulas = () => {
    switch(modeloContrato) {
      case 'prestacao_servico':
        return [
          'O CONTRATADO se compromete a executar os serviços descritos com qualidade e dentro do prazo acordado.',
          'O CONTRATANTE deverá fornecer todas as informações necessárias para a execução do serviço.',
          'Alterações no escopo do projeto poderão gerar custos adicionais mediante acordo prévio.',
          'O CONTRATADO terá até 3 dias corridos para correções após a entrega final.'
        ]
      case 'venda_produto':
        return [
          'O produto será entregue conforme especificações acordadas.',
          'Garantia de 90 dias contra defeitos de fabricação.',
          'Trocas ou devoluções mediante análise e dentro do prazo legal.',
          'Responsabilidade sobre danos durante transporte é da transportadora contratada.'
        ]
      case 'manutencao':
        return [
          'Manutenção preventiva e corretiva conforme necessidade identificada.',
          'Atendimento em horário comercial, salvo acordo em contrário.',
          'Peças e materiais utilizados serão cobrados separadamente, quando aplicável.',
          'Garantia de 30 dias sobre os serviços executados.'
        ]
      case 'consultoria':
        return [
          'Consultoria especializada conforme escopo definido.',
          'Entregas de relatórios e análises conforme cronograma acordado.',
          'Reuniões periódicas para alinhamento e acompanhamento.',
          'Confidencialidade sobre informações sensíveis do CONTRATANTE.'
        ]
      default:
        return [
          'As partes se comprometem a cumprir todas as cláusulas deste contrato.',
          'Alterações devem ser acordadas por escrito entre as partes.',
          'Em caso de inadimplência, serão aplicadas as penalidades previstas em lei.',
          'Foro competente definido para resolução de conflitos.'
        ]
    }
  }

  const gerarContratoPDF = async () => {
    if (!modeloContrato || !nomeEmpresa || !cpfCnpjEmpresa || !telefoneEmpresa || !emailEmpresa ||
        !nomeCliente || !emailCliente || !telefoneCliente || !cpfCnpjCliente ||
        !tituloServico || !descricaoDetalhada || !prazoEntrega || !formaPagamento || !foro ||
        !comprovanteUrl) {
      alert('Por favor, preencha todos os campos obrigatórios (*) e envie a comprovação de aceite')
      return
    }

    setLoading(true)

    try {
      const user = lumi.auth.user
      if (!user) {
        alert('Usuário não autenticado')
        return
      }

      const numeroContrato = `CONTRATO-${new Date().getFullYear()}-${Date.now().toString().slice(-6)}`
      const total = calcularTotal()

      // Salvar contrato no banco
      await lumi.entities.contratos.create({
        creator: user.userId,
        modeloContrato,
        nomeEmpresa,
        nomeCliente,
        emailCliente,
        cpfCnpj: cpfCnpjCliente,
        servico: tituloServico,
        descricao: descricaoDetalhada,
        valor: total.toFixed(2),
        prazo: prazoEntrega,
        formaPagamento,
        multaCancelamento: multaCancelamento || '',
        condicoesCancelamento: condicoesCancelamento || '',
        foro,
        observacoes: observacoes || '',
        comprovanteAceite: comprovanteUrl,
        tipoComprovante,
        dataCriacao: new Date().toISOString(),
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      })

      // Gerar PDF Profissional do Contrato
      const doc = new jsPDF()
      const logoUrl = 'https://static.lumi.new/94/94387bd69277769ec0567b4f6dbc1e0e.webp'
      
      // Cabeçalho
      doc.setFontSize(18)
      doc.setFont('helvetica', 'bold')
      doc.setTextColor(139, 0, 0)
      doc.text('CONTRATO DE PRESTAÇÃO DE SERVIÇOS', 105, 18, { align: 'center' })
      
      doc.setFontSize(8)
      doc.setFont('helvetica', 'normal')
      doc.setTextColor(100, 100, 100)
      doc.text(`Contrato Nº ${numeroContrato}`, 105, 25, { align: 'center' })
      doc.text(`Gerado em ${new Date().toLocaleDateString('pt-BR')}`, 105, 29, { align: 'center' })
      
      // Linha separadora
      doc.setDrawColor(139, 0, 0)
      doc.setLineWidth(0.8)
      doc.line(20, 33, 190, 33)
      
      let yPos = 40
      
      // CONTRATANTE (Empresa)
      doc.setFontSize(10)
      doc.setFont('helvetica', 'bold')
      doc.setTextColor(139, 0, 0)
      doc.text('CONTRATADO', 20, yPos)
      
      yPos += 5
      doc.setFontSize(8)
      doc.setFont('helvetica', 'bold')
      doc.setTextColor(0, 0, 0)
      doc.text(nomeEmpresa, 20, yPos)
      
      yPos += 4
      doc.setFont('helvetica', 'normal')
      doc.setTextColor(80, 80, 80)
      doc.text(`${cpfCnpjEmpresa.length <= 14 ? 'CPF' : 'CNPJ'}: ${cpfCnpjEmpresa} | Tel: ${telefoneEmpresa} | ${emailEmpresa}`, 20, yPos)
      
      if (enderecoEmpresa) {
        yPos += 3
        const enderecoCompleto = `${enderecoEmpresa}${cidadeEmpresa ? ', ' + cidadeEmpresa : ''}${estadoEmpresa ? ' - ' + estadoEmpresa : ''}${cepEmpresa ? ', ' + cepEmpresa : ''}`
        doc.text(enderecoCompleto, 20, yPos)
      }
      
      // CONTRATANTE (Cliente)
      yPos += 8
      doc.setFontSize(10)
      doc.setFont('helvetica', 'bold')
      doc.setTextColor(139, 0, 0)
      doc.text('CONTRATANTE', 20, yPos)
      
      yPos += 5
      doc.setFontSize(8)
      doc.setFont('helvetica', 'bold')
      doc.setTextColor(0, 0, 0)
      doc.text(nomeCliente, 20, yPos)
      
      yPos += 4
      doc.setFont('helvetica', 'normal')
      doc.setTextColor(80, 80, 80)
      const clienteInfo = `${cpfCnpjCliente.length <= 14 ? 'CPF' : 'CNPJ'}: ${cpfCnpjCliente} | Tel: ${telefoneCliente} | ${emailCliente}`
      doc.text(clienteInfo, 20, yPos)
      
      if (enderecoCliente) {
        yPos += 3
        doc.text(enderecoCliente, 20, yPos)
      }
      
      // OBJETO DO CONTRATO
      yPos += 8
      doc.setFontSize(10)
      doc.setFont('helvetica', 'bold')
      doc.setTextColor(139, 0, 0)
      doc.text('OBJETO DO CONTRATO', 20, yPos)
      
      yPos += 5
      doc.setFontSize(9)
      doc.setFont('helvetica', 'bold')
      doc.setTextColor(0, 0, 0)
      doc.text(tituloServico, 20, yPos)
      
      yPos += 4
      doc.setFontSize(8)
      doc.setFont('helvetica', 'normal')
      doc.setTextColor(60, 60, 60)
      const descLines = doc.splitTextToSize(descricaoDetalhada, 170)
      doc.text(descLines, 20, yPos)
      yPos += descLines.length * 3
      
      if (entregasIncluidas) {
        yPos += 3
        doc.setFont('helvetica', 'bold')
        doc.text('Entregas:', 20, yPos)
        yPos += 3
        doc.setFont('helvetica', 'normal')
        const entregasLines = doc.splitTextToSize(entregasIncluidas, 170)
        doc.text(entregasLines, 20, yPos)
        yPos += entregasLines.length * 3
      }
      
      // VALORES
      yPos += 6
      doc.setFontSize(10)
      doc.setFont('helvetica', 'bold')
      doc.setTextColor(139, 0, 0)
      doc.text('VALORES E CONDIÇÕES', 20, yPos)
      
      yPos += 5
      doc.setFontSize(8)
      doc.setFont('helvetica', 'normal')
      doc.setTextColor(60, 60, 60)
      
      // Tabela compacta
      itensOrcamento.forEach((item) => {
        if (item.descricao && item.valor) {
          doc.text(`• ${item.descricao.substring(0, 45)}: R$ ${item.valor}`, 22, yPos)
          yPos += 3
        }
      })
      
      if (valorMaoObra) {
        doc.text(`• Mão de Obra: R$ ${valorMaoObra}`, 22, yPos)
        yPos += 3
      }
      
      if (valorMateriais) {
        doc.text(`• Materiais/Insumos: R$ ${valorMateriais}`, 22, yPos)
        yPos += 3
      }
      
      if (desconto) {
        doc.setTextColor(0, 150, 0)
        doc.text(`• Desconto: - R$ ${desconto}`, 22, yPos)
        yPos += 3
        doc.setTextColor(60, 60, 60)
      }
      
      yPos += 2
      doc.setFontSize(9)
      doc.setFont('helvetica', 'bold')
      doc.setTextColor(139, 0, 0)
      doc.text(`VALOR TOTAL: R$ ${total.toFixed(2).replace('.', ',')}`, 22, yPos)
      
      yPos += 5
      doc.setFontSize(8)
      doc.setFont('helvetica', 'normal')
      doc.setTextColor(60, 60, 60)
      doc.text(`• Prazo de execução: ${prazoEntrega}`, 22, yPos)
      yPos += 3
      doc.text(`• Forma de pagamento: ${formaPagamento}`, 22, yPos)
      if (garantia) {
        yPos += 3
        doc.text(`• Garantia: ${garantia}`, 22, yPos)
      }
      
      // CLÁUSULAS
      yPos += 7
      doc.setFontSize(10)
      doc.setFont('helvetica', 'bold')
      doc.setTextColor(139, 0, 0)
      doc.text('CLÁUSULAS CONTRATUAIS', 20, yPos)
      
      const clausulas = getClausulas()
      yPos += 5
      doc.setFontSize(7)
      doc.setFont('helvetica', 'normal')
      doc.setTextColor(60, 60, 60)
      clausulas.forEach((clausula, index) => {
        const clausulaLines = doc.splitTextToSize(`${index + 1}. ${clausula}`, 170)
        doc.text(clausulaLines, 20, yPos)
        yPos += clausulaLines.length * 3
      })
      
      // CANCELAMENTO
      if (multaCancelamento || condicoesCancelamento) {
        yPos += 4
        doc.setFontSize(10)
        doc.setFont('helvetica', 'bold')
        doc.setTextColor(139, 0, 0)
        doc.text('CANCELAMENTO', 20, yPos)
        yPos += 5
        doc.setFontSize(7)
        doc.setFont('helvetica', 'normal')
        doc.setTextColor(60, 60, 60)
        if (multaCancelamento) {
          doc.text(`• Multa por cancelamento: ${multaCancelamento}%`, 22, yPos)
          yPos += 3
        }
        if (condicoesCancelamento) {
          const cancelLines = doc.splitTextToSize(`• ${condicoesCancelamento}`, 168)
          doc.text(cancelLines, 22, yPos)
          yPos += cancelLines.length * 3
        }
      }
      
      // FORO
      yPos += 4
      doc.setFontSize(7)
      doc.text(`Foro competente: ${foro}`, 20, yPos)
      
      // OBSERVAÇÕES
      if (observacoes || naoIncluso || termosAdicionais) {
        yPos += 5
        doc.setFontSize(10)
        doc.setFont('helvetica', 'bold')
        doc.setTextColor(139, 0, 0)
        doc.text('OBSERVAÇÕES', 20, yPos)
        yPos += 4
        doc.setFontSize(7)
        doc.setFont('helvetica', 'normal')
        doc.setTextColor(60, 60, 60)
        
        if (observacoes) {
          const obsLines = doc.splitTextToSize(`• ${observacoes}`, 168)
          doc.text(obsLines, 22, yPos)
          yPos += obsLines.length * 3 + 2
        }
        if (naoIncluso) {
          const naoInclusoLines = doc.splitTextToSize(`• Não incluso: ${naoIncluso}`, 168)
          doc.text(naoInclusoLines, 22, yPos)
          yPos += naoInclusoLines.length * 3 + 2
        }
        if (termosAdicionais) {
          const termosLines = doc.splitTextToSize(`• ${termosAdicionais}`, 168)
          doc.text(termosLines, 22, yPos)
          yPos += termosLines.length * 3
        }
      }
      
      // ACEITE DO CLIENTE
      yPos += 7
      doc.setFontSize(10)
      doc.setFont('helvetica', 'bold')
      doc.setTextColor(139, 0, 0)
      doc.text('ACEITE DO CLIENTE', 20, yPos)
      
      yPos += 5
      doc.setFontSize(8)
      doc.setFont('helvetica', 'normal')
      doc.setTextColor(0, 0, 0)
      doc.text(`Cliente ${nomeCliente} aceitou esta proposta em ${new Date().toLocaleDateString('pt-BR')}`, 20, yPos)
      
      yPos += 4
      doc.setFontSize(7)
      doc.setTextColor(80, 80, 80)
      doc.text(`Comprovação anexada: ${tipoComprovante === 'imagem' ? 'Print/Screenshot' : tipoComprovante === 'audio' ? 'Áudio' : 'Documento'} (arquivo digital)`, 20, yPos)
      yPos += 3
      doc.text(`URL do comprovante: ${comprovanteUrl.substring(0, 80)}...`, 20, yPos)
      
      // Assinaturas
      yPos += 12
      doc.setFontSize(8)
      doc.setTextColor(0, 0, 0)
      doc.setLineWidth(0.3)
      doc.setDrawColor(200, 200, 200)
      doc.line(25, yPos, 85, yPos)
      doc.text('Assinatura do Contratante', 55, yPos + 4, { align: 'center' })
      
      doc.line(125, yPos, 185, yPos)
      doc.text('Assinatura do Contratado', 155, yPos + 4, { align: 'center' })
      
      yPos += 10
      doc.setFontSize(7)
      doc.setTextColor(150, 150, 150)
      doc.text(`${nomeCliente}`, 55, yPos, { align: 'center' })
      doc.text(`${nomeEmpresa}`, 155, yPos, { align: 'center' })
      
      // Rodapé preto com logo pequena
      doc.setFillColor(0, 0, 0)
      doc.rect(0, 275, 210, 22, 'F')
      
      // Logo pequena no rodapé
      try {
        doc.addImage(logoUrl, 'PNG', 15, 278, 12, 12)
      } catch (e) {
        console.log('Logo no rodapé não carregada')
      }
      
      doc.setFontSize(8)
      doc.setTextColor(255, 255, 255)
      doc.text(nomeEmpresa, 105, 282, { align: 'center' })
      doc.setFontSize(7)
      doc.text(`${telefoneEmpresa} | ${emailEmpresa}`, 105, 287, { align: 'center' })
      if (websiteEmpresa) {
        doc.text(websiteEmpresa, 105, 291, { align: 'center' })
      }
      
      // Download
      doc.save(`Contrato_${numeroContrato}_${nomeCliente.replace(/\s+/g, '_')}.pdf`)
      
      setPdfGerado(true)
      setTimeout(() => {
        navigate('/dashboard')
      }, 2000)
    } catch (error) {
      console.error('Erro ao gerar contrato:', error)
      alert('Erro ao gerar contrato')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/dashboard')} className="p-2 hover:bg-gray-100 rounded-lg">
              <ArrowLeft size={20} className="text-gray-600" />
            </button>
            <div>
              <h1 className="text-xl font-bold text-[#8B0000]">Criar Novo Contrato</h1>
              <p className="text-xs text-gray-600">Gere contrato profissional em PDF</p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-6">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="space-y-6">
            
            {/* BOTÃO DE IMPORTAR PROPOSTA */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <button
                onClick={() => setMostrarImportacao(!mostrarImportacao)}
                className="w-full flex items-center justify-between text-sm font-semibold text-blue-900"
              >
                <span>📋 Importar dados de Proposta Aceita</span>
                <span>{mostrarImportacao ? '▲' : '▼'}</span>
              </button>
              
              {mostrarImportacao && (
                <div className="mt-4 space-y-3">
                  {propostasAceitas.length === 0 ? (
                    <p className="text-xs text-gray-600">Nenhuma proposta aceita encontrada.</p>
                  ) : (
                    <>
                      <select
                        value={propostaSelecionada}
                        onChange={(e) => setPropostaSelecionada(e.target.value)}
                        className="w-full px-3 py-2 text-sm border border-blue-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="">Selecione uma proposta...</option>
                        {propostasAceitas.map((p) => (
                          <option key={p._id} value={p._id}>
                            {p.nomeCliente} - {p.servicoOferecido} - R$ {p.valor}
                          </option>
                        ))}
                      </select>
                      <button
                        onClick={importarProposta}
                        disabled={!propostaSelecionada}
                        className="w-full bg-blue-600 text-white py-2 rounded-lg text-sm font-semibold hover:bg-blue-700 disabled:bg-gray-400"
                      >
                        Importar Dados
                      </button>
                    </>
                  )}
                </div>
              )}
            </div>

            {/* SEÇÃO 1: DADOS DA SUA EMPRESA */}
            <div className="border-b pb-4">
              <h2 className="text-lg font-bold text-[#8B0000] mb-4">1. Dados da Sua Empresa</h2>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Nome da Empresa *</label>
                  <input
                    type="text"
                    value={nomeEmpresa}
                    onChange={(e) => setNomeEmpresa(e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    placeholder="Digite o nome da sua empresa"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">CPF ou CNPJ *</label>
                  <input
                    type="text"
                    value={cpfCnpjEmpresa}
                    onChange={(e) => setCpfCnpjEmpresa(e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    placeholder="000.000.000-00 ou 00.000.000/0000-00"
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">Telefone/WhatsApp *</label>
                    <input
                      type="tel"
                      value={telefoneEmpresa}
                      onChange={(e) => setTelefoneEmpresa(e.target.value)}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                      placeholder="(00) 00000-0000"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">E-mail *</label>
                    <input
                      type="email"
                      value={emailEmpresa}
                      onChange={(e) => setEmailEmpresa(e.target.value)}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                      placeholder="contato@empresa.com"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Endereço Completo</label>
                  <input
                    type="text"
                    value={enderecoEmpresa}
                    onChange={(e) => setEnderecoEmpresa(e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    placeholder="Rua, número, bairro"
                  />
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                  <div className="col-span-1">
                    <label className="block text-sm font-semibold text-gray-700 mb-1">Cidade</label>
                    <input
                      type="text"
                      value={cidadeEmpresa}
                      onChange={(e) => setCidadeEmpresa(e.target.value)}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                      placeholder="Cidade"
                    />
                  </div>

                  <div className="col-span-1">
                    <label className="block text-sm font-semibold text-gray-700 mb-1">Estado</label>
                    <input
                      type="text"
                      value={estadoEmpresa}
                      onChange={(e) => setEstadoEmpresa(e.target.value)}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                      placeholder="UF"
                      maxLength={2}
                    />
                  </div>

                  <div className="col-span-2">
                    <label className="block text-sm font-semibold text-gray-700 mb-1">CEP</label>
                    <input
                      type="text"
                      value={cepEmpresa}
                      onChange={(e) => setCepEmpresa(e.target.value)}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                      placeholder="00000-000"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">Website</label>
                    <input
                      type="url"
                      value={websiteEmpresa}
                      onChange={(e) => setWebsiteEmpresa(e.target.value)}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                      placeholder="www.suaempresa.com"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">Redes Sociais</label>
                    <input
                      type="text"
                      value={redesSociais}
                      onChange={(e) => setRedesSociais(e.target.value)}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                      placeholder="@instagram | @linkedin"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* SEÇÃO 2: TIPO DE CONTRATO */}
            <div className="border-b pb-4">
              <h2 className="text-lg font-bold text-[#8B0000] mb-4">2. Tipo de Contrato</h2>
              
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">Modelo de Contrato *</label>
                <select
                  value={modeloContrato}
                  onChange={(e) => setModeloContrato(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
                >
                  <option value="">Selecione o tipo...</option>
                  <option value="prestacao_servico">Prestação de Serviço</option>
                  <option value="venda_produto">Venda de Produto</option>
                  <option value="manutencao">Manutenção</option>
                  <option value="consultoria">Consultoria</option>
                </select>
              </div>
            </div>

            {/* SEÇÃO 3: DADOS DO CLIENTE */}
            <div className="border-b pb-4">
              <h2 className="text-lg font-bold text-[#8B0000] mb-4">3. Dados do Cliente</h2>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Nome Completo ou Razão Social *</label>
                  <input
                    type="text"
                    value={nomeCliente}
                    onChange={(e) => setNomeCliente(e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    placeholder="Digite o nome do cliente"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">CPF ou CNPJ do Cliente *</label>
                  <input
                    type="text"
                    value={cpfCnpjCliente}
                    onChange={(e) => setCpfCnpjCliente(e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    placeholder="000.000.000-00 ou 00.000.000/0000-00"
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">E-mail *</label>
                    <input
                      type="email"
                      value={emailCliente}
                      onChange={(e) => setEmailCliente(e.target.value)}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                      placeholder="cliente@email.com"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">Telefone/WhatsApp *</label>
                    <input
                      type="tel"
                      value={telefoneCliente}
                      onChange={(e) => setTelefoneCliente(e.target.value)}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                      placeholder="(00) 00000-0000"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Endereço do Cliente</label>
                  <input
                    type="text"
                    value={enderecoCliente}
                    onChange={(e) => setEnderecoCliente(e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    placeholder="Rua, número, bairro, cidade - UF"
                  />
                </div>
              </div>
            </div>

            {/* SEÇÃO 4: INFORMAÇÕES DO SERVIÇO */}
            <div className="border-b pb-4">
              <h2 className="text-lg font-bold text-[#8B0000] mb-4">4. Informações do Serviço</h2>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Título do Serviço *</label>
                  <input
                    type="text"
                    value={tituloServico}
                    onChange={(e) => setTituloServico(e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    placeholder="Ex: Desenvolvimento de Website Institucional"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Descrição Detalhada do Serviço *</label>
                  <textarea
                    value={descricaoDetalhada}
                    onChange={(e) => setDescricaoDetalhada(e.target.value)}
                    rows={5}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    placeholder="Detalhe o escopo completo do trabalho, etapas, funcionalidades, métodos..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Entregas/Itens Incluídos</label>
                  <textarea
                    value={entregasIncluidas}
                    onChange={(e) => setEntregasIncluidas(e.target.value)}
                    rows={3}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    placeholder="Ex: 3 reuniões de alinhamento, 2 revisões, treinamento de 1 hora..."
                  />
                </div>
              </div>
            </div>

            {/* SEÇÃO 5: VALORES E INVESTIMENTO */}
            <div className="border-b pb-4">
              <h2 className="text-lg font-bold text-[#8B0000] mb-4">5. Valores e Investimento</h2>
              
              <div className="space-y-4">
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-sm font-semibold text-gray-700">Itens do Orçamento</label>
                    <button
                      type="button"
                      onClick={adicionarItem}
                      className="flex items-center gap-1 text-xs text-[#8B0000] hover:underline"
                    >
                      <Plus size={14} />
                      Adicionar item
                    </button>
                  </div>
                  
                  {itensOrcamento.map((item, index) => (
                    <div key={index} className="flex gap-2 items-start mb-2">
                      <input
                        type="text"
                        value={item.descricao}
                        onChange={(e) => atualizarItem(index, 'descricao', e.target.value)}
                        className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                        placeholder="Descrição do item"
                      />
                      <input
                        type="text"
                        value={item.valor}
                        onChange={(e) => atualizarItem(index, 'valor', e.target.value)}
                        className="w-32 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                        placeholder="Valor"
                      />
                      {itensOrcamento.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removerItem(index)}
                          className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                        >
                          <Trash2 size={16} />
                        </button>
                      )}
                    </div>
                  ))}
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">Mão de Obra (R$)</label>
                    <input
                      type="text"
                      value={valorMaoObra}
                      onChange={(e) => setValorMaoObra(e.target.value)}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                      placeholder="0.00"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">Materiais/Insumos (R$)</label>
                    <input
                      type="text"
                      value={valorMateriais}
                      onChange={(e) => setValorMateriais(e.target.value)}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                      placeholder="0.00"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Desconto (R$)</label>
                  <input
                    type="text"
                    value={desconto}
                    onChange={(e) => setDesconto(e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    placeholder="0.00"
                  />
                </div>

                <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-bold text-gray-700">TOTAL</span>
                    <span className="text-xl font-bold text-[#8B0000]">R$ {calcularTotal().toFixed(2).replace('.', ',')}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* SEÇÃO 6: CONDIÇÕES CONTRATUAIS */}
            <div className="border-b pb-4">
              <h2 className="text-lg font-bold text-[#8B0000] mb-4">6. Condições Contratuais</h2>
              
              <div className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">Prazo de Entrega *</label>
                    <input
                      type="text"
                      value={prazoEntrega}
                      onChange={(e) => setPrazoEntrega(e.target.value)}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                      placeholder="Ex: 30 dias ou 15/03/2025"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">Forma de Pagamento *</label>
                    <select
                      value={formaPagamento}
                      onChange={(e) => setFormaPagamento(e.target.value)}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    >
                      <option value="">Selecione...</option>
                      <option value="À vista com 10% de desconto">À vista com desconto</option>
                      <option value="Parcelado em 3x sem juros">Parcelado em 3x sem juros</option>
                      <option value="50% entrada + 50% na entrega">50% entrada + 50% na entrega</option>
                      <option value="Pix">Pix</option>
                      <option value="Boleto Bancário">Boleto Bancário</option>
                      <option value="Cartão de Crédito">Cartão de Crédito</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Garantia Oferecida</label>
                  <input
                    type="text"
                    value={garantia}
                    onChange={(e) => setGarantia(e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    placeholder="Ex: 90 dias de suporte e ajustes gratuitos"
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">Multa por Cancelamento (%)</label>
                    <input
                      type="text"
                      value={multaCancelamento}
                      onChange={(e) => setMultaCancelamento(e.target.value)}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                      placeholder="Ex: 20"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">Foro/Cidade Competente *</label>
                    <input
                      type="text"
                      value={foro}
                      onChange={(e) => setForo(e.target.value)}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                      placeholder="Ex: São Paulo - SP"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Condições de Cancelamento</label>
                  <textarea
                    value={condicoesCancelamento}
                    onChange={(e) => setCondicoesCancelamento(e.target.value)}
                    rows={2}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    placeholder="Detalhe as condições de cancelamento..."
                  />
                </div>
              </div>
            </div>

            {/* SEÇÃO 7: OBSERVAÇÕES E TERMOS */}
            <div className="border-b pb-4">
              <h2 className="text-lg font-bold text-[#8B0000] mb-4">7. Observações e Termos</h2>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Observações Importantes</label>
                  <textarea
                    value={observacoes}
                    onChange={(e) => setObservacoes(e.target.value)}
                    rows={2}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    placeholder="Informações adicionais relevantes..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">O que NÃO está incluso</label>
                  <textarea
                    value={naoIncluso}
                    onChange={(e) => setNaoIncluso(e.target.value)}
                    rows={2}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    placeholder="Ex: hospedagem, domínio, fotos profissionais..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Termos e Condições Adicionais</label>
                  <textarea
                    value={termosAdicionais}
                    onChange={(e) => setTermosAdicionais(e.target.value)}
                    rows={2}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    placeholder="Cláusulas contratuais, políticas de cancelamento..."
                  />
                </div>
              </div>
            </div>

            {/* SEÇÃO 8: COMPROVAÇÃO DE ACEITE DO CLIENTE */}
            <div className="pb-4">
              <h2 className="text-lg font-bold text-[#8B0000] mb-4">8. Comprovação de Aceite do Cliente *</h2>
              
              <div className="bg-yellow-50 border border-yellow-300 rounded-lg p-4 mb-4">
                <p className="text-xs text-yellow-800 font-semibold mb-2">⚠️ OBRIGATÓRIO: Anexe a comprovação de que o cliente aceitou a proposta</p>
                <p className="text-xs text-yellow-700">Aceito: print/screenshot de WhatsApp, e-mail, mensagem, áudio de confirmação ou documento assinado</p>
              </div>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Enviar Comprovante *
                  </label>
                  
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-[#8B0000] transition-colors">
                    <input
                      type="file"
                      id="fileUpload"
                      accept="image/*,audio/*,.pdf"
                      onChange={handleFileUpload}
                      className="hidden"
                    />
                    <label
                      htmlFor="fileUpload"
                      className="cursor-pointer flex flex-col items-center"
                    >
                      {uploadLoading ? (
                        <div className="text-gray-600">
                          <Upload className="animate-pulse mx-auto mb-2" size={32} />
                          <p className="text-sm">Enviando arquivo...</p>
                        </div>
                      ) : comprovanteUrl ? (
                        <div className="text-green-600">
                          <CheckCircle size={32} className="mx-auto mb-2" />
                          <p className="text-sm font-semibold">Comprovante enviado com sucesso!</p>
                          <p className="text-xs text-gray-600 mt-1">
                            Tipo: {tipoComprovante === 'imagem' ? 'Screenshot/Print' : tipoComprovante === 'audio' ? 'Áudio' : 'Documento'}
                          </p>
                          <p className="text-xs text-blue-600 mt-2 hover:underline">Clique para trocar arquivo</p>
                        </div>
                      ) : (
                        <div className="text-gray-600">
                          <FileUp size={32} className="mx-auto mb-2" />
                          <p className="text-sm font-semibold">Clique para enviar comprovante</p>
                          <p className="text-xs text-gray-500 mt-1">Imagem, Áudio ou PDF (max 10MB)</p>
                        </div>
                      )}
                    </label>
                  </div>
                </div>

                {comprovanteUrl && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                    <p className="text-xs text-green-800">
                      <strong>Arquivo anexado:</strong> {comprovanteAceite?.name || 'arquivo.jpg'}
                    </p>
                  </div>
                )}
              </div>
            </div>

            <button
              onClick={gerarContratoPDF}
              disabled={loading || !comprovanteUrl}
              className="w-full bg-[#8B0000] text-white py-3 rounded-lg font-semibold hover:bg-[#6B0000] transition-colors disabled:bg-gray-400 flex items-center justify-center gap-2 text-sm mt-6"
            >
              {loading ? (
                'Gerando Contrato...'
              ) : (
                <>
                  <FileSignature size={18} />
                  Gerar Contrato Profissional em PDF
                </>
              )}
            </button>

            {pdfGerado && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
                <Download size={32} className="mx-auto text-green-600 mb-2" />
                <p className="text-sm font-semibold text-green-800">Contrato gerado e baixado com sucesso!</p>
                <p className="text-xs text-green-600 mt-1">Redirecionando para o dashboard...</p>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  )
}
